// Thin fetch wrappers for the Hype-Lab backend. All paths are relative —
// Vite's dev proxy (or your prod reverse proxy) forwards them to the API
// host. That keeps the SPA same-origin with the backend so the signed
// session cookie and OAuth round-trip work without CORS gymnastics.

const json = (r) => r.json();
const jsonStatus = async (r) => ({ status: r.status, body: await r.json() });

const post = (path, body) =>
  fetch(path, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });

export const api = {
  /** Bootstrap data: model catalog, hook styles, defaults. */
  config:           ()      => fetch('/api/lab/config').then(json),

  /** Mock auth + identity. The Hype-Lab backend's signup is mock — no
   *  password — and just establishes a signed cookie tying this browser
   *  to the given email. We feed it a per-browser anonymous email so the
   *  cookie carries a stable persona/credits across reloads. */
  signup:           (body)  => post('/api/lab/auth/signup', body).then(json),
  logout:           ()      => post('/api/lab/auth/logout').then(json),
  me:               ()      => fetch('/api/lab/me').then(r => r.ok ? r.json() : null),

  /** Persona CRUD (single persona per session). */
  getPersona:       ()      => fetch('/api/lab/personas/me').then(json),
  savePersona:      (body)  => post('/api/lab/personas/me', body).then(json),
  // Aliases for the historical names used in main.js.
  persona:          (body)  => post('/api/lab/personas/me', body).then(json),

  /** X OAuth (real round-trip). After X.com authorizes, the backend's
   *  callback redirects to `return_to` — which here is THIS SPA's origin
   *  so the user lands back on the same domain. The backend's host
   *  allowlist controls which return_to URLs are accepted. */
  oauthStart:       ()      => post('/api/lab/x/oauth/start', { return_to: window.location.origin + '/' }).then(json),
  oauthDisconnect:  ()      => post('/api/lab/x/oauth/disconnect').then(json),
  lists:            ()      => fetch('/api/lab/x/lists').then(jsonStatus),

  /** Wallet — mock-credit on the backend; gating happens server-side. */
  topup:            (amount) => post('/api/lab/credits/topup', { amount }).then(jsonStatus),

  /** Generation pipeline (accepts `preview: true` for a free fast preview). */
  prefetchSource:   (body)  => post('/api/lab/source/prefetch', body).then(json).catch(() => null),
  prefetch:         (body)  => post('/api/lab/source/prefetch', body).then(json).catch(() => null),
  generate:         (body)  => post('/api/lab/generate', body).then(jsonStatus),
  job:              (id)    => fetch('/api/lab/jobs/' + id).then(json),
  jobsList:         ()      => fetch('/api/lab/jobs').then(json),

  /** Boot the loginless session. Calls `api.me` first — if the backend
   *  cookie is already wired up for this browser, we're done. Otherwise
   *  signs up with the stable anon email so the cookie is created.
   *
   *  Returns { user, persona } or null on hard failure. */
  ensureAnonSession: async (anonEmail) => {
    if (!anonEmail) return null;
    let me = null;
    try { me = await api.me(); } catch { /* network blip */ }
    if (me?.user?.email === anonEmail) return me;
    try {
      const r = await api.signup({ email: anonEmail, name: 'TruthU user' });
      return r || null;
    } catch (e) {
      console.warn('Anon session bootstrap failed:', e);
      return null;
    }
  },
};
